import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import dayjs from 'dayjs';
import {
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Box
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

export default function SingleDateRangePicker2({ onDateRangeChange, maxDay, defaultStartDate, defaultEndDate }) {
  const inputRef = useRef(null);

  const today = dayjs().endOf('day').toDate();
  const oneYearAgo = dayjs().subtract(1, 'year').startOf('day').toDate();

  // Set start date to 1 year ago, end date to today by default
  const [startDate, setStartDate] = useState(dayjs().startOf('month').toDate());
  const [endDate, setEndDate] = useState(today);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [calendarWidth, setCalendarWidth] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (inputRef.current) {
      setCalendarWidth(inputRef.current.offsetWidth);
    }

    // Notify parent on mount with default range
    onDateRangeChange?.({ startDate: defaultStartDate || oneYearAgo, endDate: defaultEndDate || today });
  }, []);

  const formattedDateRange =
    (defaultStartDate || startDate) && (defaultEndDate || endDate)
      ? `${dayjs(defaultStartDate || startDate).format('MM/DD/YYYY')} - ${dayjs(defaultEndDate || endDate).format('MM/DD/YYYY')}`
      : '';

  const handleOnChange = (dates) => {
    const [start, end] = dates;

    if (start && end) {
      const diff = dayjs(end).diff(dayjs(start), 'day');
      if (diff > maxDay) {
        setError(true);
        return;
      }
      setError(false);
    }

    setStartDate(start);
    setEndDate(end);

    if (end) {
      setCalendarVisible(false);
    }

    onDateRangeChange?.({ startDate: start, endDate: end });
  };

  const toggleCalendarVisibility = (e) => {
    e.stopPropagation();
    setCalendarVisible((prev) => !prev);
  };

  const handleBlur = () => {
    if (!startDate || !endDate) {
      setError(true);
    }
  };

  return (
    <FormControl fullWidth margin="normal" style={{ position: 'relative' }}>
      <style>{`
        .react-datepicker__month-container {
          width: 22em !important;          
        }
      `}</style>

      <TextField
        inputRef={inputRef}
        value={formattedDateRange}
        onClick={toggleCalendarVisibility}
        onBlur={handleBlur}
        error={error}
        helperText={error ? 'Date range cannot exceed 31 days.' : ''}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleCalendarVisibility}>
                <CalendarTodayIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        placeholder="Select a date range"
        variant="outlined"
        label=""
      />

      {calendarVisible && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 10,
            mt: 1,
            boxShadow: 3,
            borderRadius: 1,
            backgroundColor: 'white',
            width: calendarWidth || 'auto',
          }}
        >
          <DatePicker
            selected={startDate}
            onChange={handleOnChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            inline
            minDate={oneYearAgo}
            maxDate={today}
            filterDate={(date) => date >= oneYearAgo && date <= today}
          />
        </Box>
      )}
    </FormControl>
  );
}
